import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Shield, Briefcase, Building2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { openGlobalAuthModal } from '@/components/auth/GlobalAuthEnforcer';
import AppSidebar from '@/components/layout/AppSidebar';
import AdminDashboard from '@/components/admin/AdminDashboard';
import TeacherDashboard from '@/components/teacher/TeacherDashboard';
import StudentAssignmentsPanel from '@/components/student/StudentAssignmentsPanel';
import NotificationPanel from '@/components/student/NotificationPanel';
import DashboardHub from '@/components/dashboard/DashboardHub';
import CosmicBackground from '@/components/layout/CosmicBackground';

const Index = () => {
  const navigate = useNavigate();
  const { user, metadata, isModerator, isTeacher } = useAuth();

  const [isAdminOpen, setIsAdminOpen] = useState(false);
  const [isTeacherOpen, setIsTeacherOpen] = useState(false);
  const [isAssignmentsOpen, setIsAssignmentsOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  return (
    <div className="relative min-h-screen w-full overflow-y-auto overflow-x-hidden flex flex-col font-sans bg-[#090a0f] text-white">
      <CosmicBackground />

      <AppSidebar 
        onOpenAdmin={() => setIsAdminOpen(true)}
        onOpenTeacher={() => setIsTeacherOpen(true)}
      />

      <header className="fixed top-0 left-0 right-0 z-40 pl-24 pr-6 py-4 border-b border-white/10 bg-[#090a0f]/80 backdrop-blur-2xl">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3.5">
            <div className="w-10 h-10 rounded-2xl bg-white text-[#090a0f] flex items-center justify-center font-black text-xs shadow-lg shadow-white/10">
              SP
            </div>
            <div className="flex flex-col">
              <span className="text-xs font-bold text-white uppercase tracking-wider leading-none">
                Study Platform
              </span>
              <span className="text-[10px] text-white/50 uppercase tracking-widest mt-1">
                Ecosystem & Assessment
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate('/assessment')}
              className="glass-btn-secondary hidden md:flex items-center gap-2 px-4 py-2 rounded-xl text-xs uppercase tracking-wider"
            >
              <Building2 className="w-3.5 h-3.5" />
              <span>Оценка компетенций</span>
            </button>

            <button
              type="button"
              onClick={() => navigate('/interview')}
              className="glass-btn-secondary hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl text-xs uppercase tracking-wider"
            >
              <Briefcase className="w-3.5 h-3.5" />
              <span>Собеседование</span>
            </button>

            {(isModerator || isTeacher) && (
              <button
                type="button"
                onClick={() => isModerator ? setIsAdminOpen(true) : setIsTeacherOpen(true)}
                className="glass-btn-secondary flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-amber-400 text-xs uppercase tracking-wider"
              >
                <Shield className="w-3.5 h-3.5" />
                <span>Панель</span>
              </button>
            )}

            {user ? (
              <button
                type="button"
                onClick={() => navigate('/profile')}
                className="glass-btn-secondary flex items-center gap-2 px-3.5 py-1.5 rounded-xl"
              >
                <div className="w-7 h-7 rounded-lg bg-white text-[#07030e] flex items-center justify-center text-xs font-bold">
                  <User className="w-3.5 h-3.5" />
                </div>
                <div className="hidden sm:flex flex-col text-left">
                  <span className="text-xs font-semibold text-white leading-none">{metadata?.fullName || 'Профиль'}</span>
                  <span className="text-[9px] text-white/50 mt-0.5">{metadata?.id}</span>
                </div>
              </button>
            ) : (
              <button
                type="button"
                onClick={() => openGlobalAuthModal()}
                className="glass-btn-primary px-5 py-2 rounded-xl text-xs uppercase tracking-wider"
              >
                Войти
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="relative z-30 w-full pl-20 transition-all duration-300">
        <DashboardHub />
      </div>

      <footer className="w-full border-t border-white/10 py-6 px-6 pl-24 bg-[#090a0f]/80 backdrop-blur-xl text-center text-xs text-white/50">
        Study Platform · Независимый центр оценки и развития компетенций
      </footer>

      {isAssignmentsOpen && (
        <StudentAssignmentsPanel
          isOpen={isAssignmentsOpen}
          onClose={() => setIsAssignmentsOpen(false)}
        />
      )}

      {isNotificationsOpen && (
        <NotificationPanel
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          onUnreadCountChange={setUnreadCount}
        />
      )}

      {isAdminOpen && (
        <AdminDashboard
          isOpen={isAdminOpen}
          onClose={() => setIsAdminOpen(false)}
        />
      )}

      {isTeacherOpen && (
        <TeacherDashboard
          isOpen={isTeacherOpen}
          onClose={() => setIsTeacherOpen(false)}
        />
      )}
    </div>
  );
};

export default Index;
